import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Sparkles, CheckCircle, ArrowRight, Loader2, Lightbulb, Star } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuizzesPage = () => {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);

  useEffect(() => {
    fetchData();
    fetchSuggestions();
  }, []);

  const fetchData = async () => {
    try {
      const [quizzesRes, resultsRes] = await Promise.all([
        axios.get(`${API_URL}/quizzes`),
        axios.get(`${API_URL}/quizzes/results/me`)
      ]);
      setQuizzes(quizzesRes.data);
      setResults(resultsRes.data);
    } catch (error) {
      console.error('Failed to fetch quizzes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const response = await axios.get(`${API_URL}/quizzes/suggestions`);
      setSuggestions(response.data);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const getQuizResult = (quizId) => {
    return results.find(r => r.quiz_id === quizId);
  };

  const completedCount = results.length;
  const totalCount = quizzes.length;
  const completionPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const categoryColors = {
    'Compatibility': 'bg-rose-50 text-rose-600',
    'Psychology': 'bg-blue-50 text-blue-600',
    'Communication': 'bg-green-50 text-green-600',
    'Values': 'bg-purple-50 text-purple-600',
    'Intimacy': 'bg-amber-50 text-amber-600'
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="quizzes-page">
        <div className="mb-8">
          <h1 className="text-3xl font-serif font-semibold mb-2">Compatibility Quizzes</h1>
          <p className="text-muted-foreground">Complete quizzes to improve your match accuracy</p>
        </div>

        {/* Progress Card */}
        <Card className="rounded-2xl border-primary/20 bg-primary/5 shadow-soft mb-8">
          <CardContent className="py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <Sparkles className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-lg">Quiz Progress</h3>
                  <p className="text-sm text-muted-foreground">
                    {completedCount} of {totalCount} quizzes completed
                  </p>
                </div>
              </div>
              <div className="w-full sm:w-48">
                <Progress value={completionPercentage} className="h-3" />
                <p className="text-xs text-muted-foreground mt-1 text-right">
                  {Math.round(completionPercentage)}% complete
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* AI Suggestions Section */}
        {suggestions && suggestions.suggestions && suggestions.suggestions.length > 0 && (
          <Card className="rounded-2xl border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-soft mb-8" data-testid="ai-suggestions">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Lightbulb className="w-5 h-5 text-amber-600" />
                <span>Recommended For You</span>
                <Badge className="ml-auto bg-amber-100 text-amber-700 rounded-full text-xs">AI Powered</Badge>
              </CardTitle>
              {suggestions.message && (
                <p className="text-sm text-amber-700/80 mt-1">{suggestions.message}</p>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {suggestions.suggestions.map((suggestion, index) => (
                  <div 
                    key={suggestion.quiz?.id || index}
                    className="p-4 rounded-xl bg-white/80 border border-amber-200/50 hover:border-amber-300 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => navigate(`/quizzes/${suggestion.quiz.id}`)}
                    data-testid={`suggested-quiz-${suggestion.quiz?.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <Star className="w-4 h-4 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm mb-1 truncate">{suggestion.quiz?.title}</h4>
                        <p className="text-xs text-muted-foreground line-clamp-2">{suggestion.reason}</p>
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      className="w-full mt-3 rounded-full bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      Take This Quiz
                      <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {loadingSuggestions && (
          <Card className="rounded-2xl border-border/50 bg-muted/30 shadow-soft mb-8">
            <CardContent className="py-6">
              <div className="flex items-center gap-3 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Getting personalized quiz recommendations...</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* All Quizzes Section */}
        <div className="mb-4">
          <h2 className="text-xl font-serif font-medium">All Quizzes</h2>
        </div>

        {/* Quizzes Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {quizzes.map((quiz) => {
            const result = getQuizResult(quiz.id);
            const isCompleted = !!result;
            
            return (
              <Card 
                key={quiz.id}
                className={`rounded-2xl border-border/50 shadow-soft transition-all ${
                  isCompleted ? 'opacity-80' : 'hover-lift cursor-pointer'
                }`}
                onClick={() => !isCompleted && navigate(`/quizzes/${quiz.id}`)}
                data-testid={`quiz-card-${quiz.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <Badge className={`rounded-full ${categoryColors[quiz.category] || 'bg-gray-50 text-gray-600'}`}>
                      {quiz.category}
                    </Badge>
                    {isCompleted && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <h3 className="text-lg font-medium mb-2">{quiz.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{quiz.description}</p>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {quiz.questions?.length || 0} questions
                    </span>
                    
                    {isCompleted ? (
                      <Badge variant="secondary" className="rounded-full">
                        Completed
                      </Badge>
                    ) : (
                      <Button 
                        size="sm" 
                        className="rounded-full"
                        data-testid={`start-quiz-${quiz.id}`}
                      >
                        Take Quiz
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {quizzes.length === 0 && (
          <Card className="rounded-2xl border-border/50 shadow-soft">
            <CardContent className="py-16 text-center">
              <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-serif font-medium mb-2">No quizzes available</h3>
              <p className="text-muted-foreground">Check back later for new quizzes!</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default QuizzesPage;
